import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';

import { API_CONFIG, AppRoutes, ERROR_UNEXPECTED } from '@/constants';
import { navigate } from '@/providers';
import { convertDatesToISO } from '@/helpers';

import { Alert } from '@/lib/ui';

const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: convert all api requests to snake_case
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const newConfig = { ...config };

  // Skip conversion for multipart/form-data
  if (newConfig.headers?.['Content-Type'] === 'multipart/form-data') {
    return newConfig;
  }

  // Convert params to snake_case
  if (config.params) {
    newConfig.params = decamelizeKeys(convertDatesToISO(config.params, 'YYYY-MM-DDTHH:mm:ssZ'));
  }

  // Convert request data to snake_case — but only for JSON-serializable payloads.
  // Binary streams (File/Blob/ArrayBuffer/Uint8Array) must be passed through unchanged
  // so the browser can stream them via XHR without round-tripping through JS memory.
  if (config.data) {
    const isBinaryPayload =
      config.data instanceof Blob ||
      config.data instanceof ArrayBuffer ||
      config.data instanceof FormData ||
      ArrayBuffer.isView(config.data);

    if (!isBinaryPayload) {
      newConfig.data = decamelizeKeys(convertDatesToISO(config.data, 'YYYY-MM-DDTHH:mm:ssZ'));
    }
  }

  return newConfig;
});

// Response interceptor: handle errors globally
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Convert response data to camelCase
    if (response.data && String(response.headers['content-type']).includes('application/json')) {
      response.data = camelizeKeys(response.data);
    }
    return response;
  },
  async (e: object) => {
    const error = camelizeKeys(e) as AxiosError;
    const skipAlert = error.config?.skipErrorAlert;

    // If unauthorized, redirect to login page
    if (error.response?.status === 401) {
      navigate(AppRoutes.LOGIN);
      return Promise.reject(error);
    }

    // If forbidden, redirect to forbidden page
    if (error.response?.status === 403) {
      navigate(AppRoutes.FORBIDDEN, { replace: true });
      return Promise.reject(error);
    }

    // If internal server error, redirect to internal server error page
    if (error.response?.status === 500 && !skipAlert) {
      navigate(AppRoutes.INTERNAL_SERVER_ERROR, { replace: true });
      return Promise.reject(error);
    }

    // Show error alert for other errors (except auth OTP endpoints)
    const response = camelizeKeys(error.response?.data) as { errorMessage?: string };
    const errorMessage = response?.errorMessage || error.message || ERROR_UNEXPECTED;
    if (!skipAlert) {
      Alert.error(errorMessage);
    }

    if (error.response) {
      error.response.data = camelizeKeys(error.response.data);
    }

    return Promise.reject(error);
  },
);

export default api;
